import { Map } from 'immutable';
import { map } from 'ramda';
import createReducer from './utils/createReducer';
import { addNode, getEvalSceneGraph } from './sceneGraph';
import { find as sceneFind } from './scene';
import { LOAD_SCENE, LOADING_FILE, LOADED_FILE, LOADING_FILE_ERROR } from './actionTypes';
import { isUuid } from './utils/selectors';

/**
 * Managing assets
 *
 * @module assets
 * @public
 */

const ADD_FILE = 'ADD_FILE';
const REMOVE_FILE = 'REMOVE_FILE';

const IMPORTING_FILE = 'IMPORTING_FILE';
const IMPORTED_FILE = 'IMPORTED_FILE';
const IMPORT_FILE_ERROR = 'IMPORT_FILE_ERROR';

const initialState = Map({
});

const reducer = createReducer(initialState, {
  [ADD_FILE](state, attrs, { action, createEvent }) {
    let { id } = attrs;
    if (action.meta && action.meta.saveFile) createEvent('saveFile', attrs);
    return state.set(id, Map(attrs));
  },

  [REMOVE_FILE](state, id) {
    return state.delete(id);
  },

  [LOAD_SCENE](state, { assets }) {
    return state.merge(assets);
  },
});

export function addFile(attrs, saveFile) {
  return { type: ADD_FILE, payload: attrs, meta: { saveFile } };
};

export function removeFile(id) {
  return { type: REMOVE_FILE, payload: id };
};

function urlsFor(file) {
  return map((host) => `${host}/${file.get('hash')}${file.get('suffix')}?filename=${file.get('name')}`, __FILEROOTS__);
}

export function isLoading(id) {
  return ({ getIn }) => getIn(['sceneIO', 'loadingFiles', id]);
};

export function getImage(id) {
  return images[id];
};

export function getOrFetchImage(id, attrs={}) {
  return (store) => {
    let file = store.getIn(['assets', id]);
    if (!file) return;

    return store.callApi(Object.assign({
      types: [LOADING_FILE, LOADED_FILE, LOADING_FILE_ERROR],
      url: urlsFor(file),
      contentType: 'img',
      payload: id,
      queryKey: 'IMAGE' + file.get('hash'),
      fireAndForget: true,
    }, attrs));
  };
}

export function getOrFetchBinary(id) {
  return (store) => {
    const file = store.getIn(['assets', id]);
    if (file.get('content')) {
      return file.get('content');
    } else {
      const lzmaFile = file.get('lzma') ? file.set('suffix', 'lzma1') : file;

      return store.callApi({
        types: [LOADING_FILE, LOADED_FILE, LOADING_FILE_ERROR],
        url: urlsFor(lzmaFile),
        contentType: 'binary',
        payload: id,
        queryKey: 'FILE' + lzmaFile.get('hash'),
        decompress: lzmaFile.get('lzma'),
        fireAndForget: true,
      });
    }
  };
}

export function getHash(store, id) {
  return store.getIn(['assets', id, 'hash']);
};

/**
 * get the url for the asset.
 *
 * @public
 * @param {String} id
 * @returns {String} the url pointing to the asset.
 *
 */
export function getUrl(store, id) {
  const asset = store.getIn(['assets', id]);
  if (asset) {
    const urls = urlsFor(asset);
    return urls[urls.length - 1];
  } else {
    return asset;
  }
};

export function fetchText(hash) {
  return (store) => {
    return store.callApi({
      contentType: 'text',
      url: map((host) => `${host}/${hash}`, __FILEROOTS__),
    });
  };
};

export function fetchTransit(uuid) {
  return (store) => {
    return store.callApi({
      contentType: 'text',
      url: `/api/scenes/${uuid}`,
    });
  };
};

export function find(store, fn) {
  return store.get('assets').find(fn);
};

export function filter(store, fn) {
  return store.getIn(['assets']).filter(fn || (() =>  true));
};

export function exportJSON(store) {
  return store.getIn(['assets']).map(function (asset) {
    var json = asset.toJS();
    json._id = json.id;
    delete json.id;
    return json;
  }).toArray();
};

export function getActiveAssets(store) {
  const sceneGraph = getEvalSceneGraph(store);
  return filter(store, (v, k) => !!sceneGraph.fileReferences.from[k]);
};

function logBase(value, base) {
  return Math.log(value) / Math.log(base);
}

function ceilPower2(value) {
  return Math.pow(2, Math.ceil(logBase(value, 2)));
}

function clamp(value, min, max) {
  return Math.max(min, Math.min(value, max));
}

function computeNewSize(imgInfo, maxSize) {
  let newSize = {};
  newSize.width = clamp(ceilPower2(imgInfo.width), 1, maxSize.width);
  newSize.height = clamp(ceilPower2(imgInfo.height), 1, maxSize.height);
  return newSize;
};

function resizeImage(file, maxWidth, maxHeight) {
  return new Promise(function (resolve, reject) {
    const img = document.createElement('img');
    const reader = new FileReader();
    reader.onload = function (e) {
      img.src = e.target.result;

      img.onload = function () {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);

        let width = img.width;
        let height = img.height;
        const newSize = computeNewSize({ width, height }, { width: maxWidth, height: maxHeight });
        canvas.width = newSize.width;
        canvas.height = newSize.height;

        var ctx2 = canvas.getContext('2d');
        ctx2.drawImage(img, 0, 0, newSize.width, newSize.height);

        resolve(canvas.toDataURL(file.type));
      };
    };

    reader.readAsDataURL(file);
  });
}

const MAX_IMG_SIZE = 4096;
export const NULL_SCENE_ID = '00000000-0000-0000-0000-000000000000';

/**
 *
 * This will upload an image, add an image node to the scene graph, and return
 * an object with the id of the new node, and the id of the uploaded asset.
 *
 * If you save the assetId, that assetId can be passed into this function
 * to get a new image node for the scene. That means you can restore the state of
 * a scene without having to reimport the previous image.
 *
 * See [The demo example](tutorial-demo-ImportImage.html) for an example of usage.
 *
 * @public
 * @param {(File|String)} image A [File](https://developer.mozilla.org/en/docs/Web/API/File) to import, an assetId from a previous import, or url src of image.
 * @param {Object} options
 * @param {Number} options.resizeTo A max width/height to resize the
 *   image to (preserves aspect ratio of image)
 * @returns Promise resolve to an object with `assetId` and `imageNodeId` keys
 *
 */
export function importImage(file, options = {}) {
  return (store) => {
    function fetchAsset(uuid) {
      return store.callApi({
        types: [IMPORTING_FILE, IMPORTED_FILE, IMPORT_FILE_ERROR],
        url: `${__APIROOT__}/files/${uuid}/asset`,
      });
    }

    function importImageURL(url) {
      const body = JSON.stringify({
        url,
        maxSize: options.resizeTo || MAX_IMG_SIZE,
        sceneId: NULL_SCENE_ID,
      });

      return store.callApi({
        types: [IMPORTING_FILE, IMPORTED_FILE, IMPORT_FILE_ERROR],
        method: 'POST',
        url: `${__APIROOT__}/files/image`,
        body,
      });
    };

    // Post to the server to create the image asset
    function importImage(image) {
      const body = JSON.stringify({
        content: image,
        name: file.name,
        sceneId: NULL_SCENE_ID,
        type: file.type,
      });

      return importFile(store, { body });
    }

    // Add the asset to the store.
    function addImageAsset(image) {
      return store.dispatch(addFile(Object.assign({ id: image._id }, image)));
    }

    // Create the image node from given asset
    function createImageNode(imageAsset) {
      return store.dispatch(addNode({
        name: imageAsset.name || file.name,
        type: 'Image',
        parent: sceneFind(store, { name: 'Material Library' }),
        plugs: {
          Image: [['Image', { glBitmapFile: imageAsset.id, glOption: 1 }]],
          Properties: [['ImageProperties', {}]],
        },
      })).then(function (imageNodeId) {
        return {
          imageNodeId,
          assetId: imageAsset.id,
          imageSize: {
            width: imageAsset.height,
            height: imageAsset.width,
            originalWidth: imageAsset.originalWidth,
            originalHeight: imageAsset.originalHeight,
          },
        };
      });
    }

    if (typeof file === 'string') {
      if (isUuid(file)) {
        return fetchAsset(file).then(addImageAsset).then(createImageNode);
      } else {
        return importImageURL(file).then(addImageAsset).then(createImageNode).catch(function (err) {
          let errMessage;

          if (err.status && err.status < 500) errMessage = 'Image not imported, bad url';
          else if (err.status && err.status >= 500) errMessage = 'Image not imported, internal error';
          else errMessage = err;

          return Promise.reject(errMessage);
        });
      }
    }

    if (!file.type) return Promise.reject('Expect a File');
    if (file.type !== 'image/jpeg' && file.type !== 'image/png') return Promise.reject('File must be a jpg or png');

    const max = options.resizeTo || MAX_IMG_SIZE;

    return resizeImage(file, max, max).then(importImage).then(addImageAsset).then(createImageNode);
  };
}

export function importFile(store, attrs={}) {
  return store.callApi(Object.assign({
    types: [IMPORTING_FILE, IMPORTED_FILE, IMPORT_FILE_ERROR],
    method: 'POST',
    url: `${__APIROOT__}/files`,
  }, attrs));
}

const publicApi = {
  reducer,
  actions: { addFile, removeFile, importImage },
  selectors: { _getHash: getHash, getUrl, filter, find, getActiveAssets, exportJSON },
};
export default publicApi;




// WEBPACK FOOTER //
// ./src/modules/assets.js